import dayjs from 'dayjs';

import { useEffect, useMemo, useState } from 'react';

import SearchInput from 'components/field/SearchInput';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';

import { AI_ASSISTANT } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './ChatHistory.styled';

const groupChatsByDate = (chats) => {
	const grouped = { today: [], yesterday: [], last7Days: [] };
	chats?.forEach((chat) => {
		const chatDate = dayjs(chat.createdDate);
		if (chatDate.isSame(dayjs(), 'day')) {
			grouped.today.push(chat);
		} else if (chatDate.isSame(dayjs().subtract(1, 'day'), 'day')) {
			grouped.yesterday.push(chat);
		} else if (chatDate.isAfter(dayjs().subtract(7, 'day'))) {
			grouped.last7Days.push(chat);
		}
	});
	return grouped;
};

const filterChatsBySearchKey = (chats, searchKey) => {
	if (!searchKey) return chats;
	return chats.filter((chat) => chat.promptHeader.toLowerCase().includes(searchKey.toLowerCase()));
};

const ChatsHistory = ({ setIsLoading, setMessages, setChatId }) => {
	const [historySearchKey, setHistorySearchKey] = useState('');
	const debouncedSearchKey = useDebounce(historySearchKey, 300);
	const [historyChats, setHistoryChats] = useState([]);
	const [isListLoading, setIsListLoading] = useState(false);
	const { showError } = useAlert();

	const filteredChats = useMemo(
		() => filterChatsBySearchKey(historyChats, debouncedSearchKey),
		[historyChats, debouncedSearchKey]
	);
	const groupedChats = useMemo(() => groupChatsByDate(filteredChats), [filteredChats]);
	const axios = useAxiosPrivate();

	useEffect(() => {
		const fetchHistoryChats = async () => {
			setIsListLoading(true);
			try {
				const response = await axios.post(AI_ASSISTANT.GET_HISTORY_CHAT_LIST);
				if (response.status === 200 && response.data) {
					setHistoryChats(response.data);
				} else {
					AppLogger.error(response);
					setHistoryChats([]);
				}
			} catch (error) {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || 'errorWhileFetchingHistory'
				});
			} finally {
				setIsListLoading(false);
			}
		};
		fetchHistoryChats();
	}, [axios]);

	const connectToHistoryChat = async (item) => {
		setMessages([]);
		setIsLoading(true);

		const fetchConversation = async () => {
			try {
				const url = `${AI_ASSISTANT.GET_CHAT_BY_ID}/${item.sessionId}/30/asc`;
				const response = await axios.get(url);
				const data = response.data;

				if (data?.history) {
					const formattedMessages = data.history.map(({ role, content, sentAt, chatId, feedbackScore }) => ({
						sender: role === 'user' ? 'User' : 'AI',
						text: content,
						timestamp: sentAt,
						chatId,
						feedbackScore
					}));

					setMessages(formattedMessages);
				}
				setChatId(item.sessionId);
			} catch (error) {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || 'errorWhileConnectingToHistoryChat'
				});
			} finally {
				setIsLoading(false);
			}
		};

		await fetchConversation();
	};

	return (
		<>
			<S.Divider />
			<S.HistoryWrapper>
				<S.HistoryHeaderWrapper>
					<SearchInput
						value={historySearchKey}
						setValue={setHistorySearchKey}
						placeholder={'search'}
						className="history-search-input"
					/>
					<Typography variant="p1" fontWeight="700">
						chatHistory
					</Typography>
				</S.HistoryHeaderWrapper>
				<S.ChatListWrapper>
					{isListLoading ? (
						<Skeleton height="2rem" width="80%" className="mx-5" />
					) : (
						Object.keys(groupedChats).some((key) => groupedChats[key].length > 0) &&
						Object.entries(groupedChats)
							.filter(([_, chats]) => chats.length > 0)
							.map(([group, chats]) => (
								<S.ChatHistoryBlock key={group}>
									<Typography variant="caption" fontWeight="700">
										{group === 'today'
											? 'today'
											: group === 'yesterday'
												? 'yesterday'
												: 'last7Days'}
									</Typography>
									<S.ChatList>
										{chats
											.slice()
											.reverse()
											.map((item, index) => (
												<S.ChatTitle key={index} onClick={() => connectToHistoryChat(item)}>
													<Typography variant="p3" fontWeight="500" noWrap>
														{item.promptHeader}
													</Typography>
												</S.ChatTitle>
											))}
									</S.ChatList>
								</S.ChatHistoryBlock>
							))
					)}
				</S.ChatListWrapper>
			</S.HistoryWrapper>
		</>
	);
};

export default ChatsHistory;
